import { useState } from 'react';
import { ArrowUpRight, Clock, Facebook, Instagram, Mail, MapPin, Phone, Star } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import { MaskedLinesOnView, Reveal } from '../components/Reveal';
import {
  ADDRESS_LINE_1,
  ADDRESS_LINE_2,
  EMAIL,
  FACEBOOK_URL,
  GOOGLE_REVIEW_URL,
  HOURS,
  INSTAGRAM_URL,
  JANE_URL,
  MAPS_URL,
  PHONE,
  PHONE_HREF,
} from '../data/site';

const TOPICS = [
  'Massage appointment question',
  'VA Community Care / veteran services',
  'Virtual doula waitlist',
  'Body sculpting interest list',
  'Massage Practice Hub coaching',
  'Something else',
];

const FIRST_VISIT = [
  {
    num: '01',
    title: 'Schedule & paperwork',
    text: 'Book your first appointment online, and watch for intake forms by email — complete them at your convenience, or in person when you arrive.',
  },
  {
    num: '02',
    title: 'A warm welcome',
    text: 'When you arrive, our receptionist greets you and invites you to relax in the waiting area while your therapist prepares for your session.',
  },
  {
    num: '03',
    title: 'Intake & conversation',
    text: 'Your therapist meets with you to review your intake information and discuss your concerns, goals, and areas of focus.',
  },
  {
    num: '04',
    title: 'Your tailored session',
    text: 'Every minute is shaped around your individual needs — you leave feeling informed, comfortable, and cared for.',
  },
];

const inputClass =
  'w-full rounded-2xl border border-brand-ink/15 bg-brand-cream px-5 py-4 text-sm text-brand-ink placeholder:text-brand-moss/50 transition-[border-color,box-shadow] duration-300 focus:border-brand-teal focus:shadow-[0_0_0_3px_rgba(32,86,82,0.12)] focus:outline-none';

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', topic: TOPICS[0], message: '' });
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, form);
      toast.success('Message sent — we’ll be in touch soon.', {
        description: 'For faster scheduling, use the Jane booking link anytime.',
      });
      setForm({ name: '', email: '', phone: '', topic: TOPICS[0], message: '' });
    } catch (err) {
      toast.error('Something went wrong.', {
        description: `Please try again, or call us at ${PHONE}.`,
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <SEO
        title="Contact Us | Thula Doula Wellness — Humble, TX"
        description="Reach Thula Doula Wellness: 5810 Wilson Rd Suite 106, Humble, TX 77396. Call (832) 350-8516, email Info@thuladoulamassage.com, or send a message."
      />
      <PageHeader
        testId="contact-page-header"
        eyebrow="Contact Us"
        lines={['Say hello.', "We'll take it from there."]}
        sub="Questions about a session, a referral, or the waitlist? Send a message — or just book and ask in person."
      />

      <section className="bg-brand-cream pb-16 md:pb-24" data-testid="first-visit-section">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <div className="rounded-3xl bg-brand-parchment p-8 md:p-14">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <MaskedLinesOnView
                as="h2"
                lines={['Your first visit.']}
                className="font-display text-[clamp(2.2rem,4.5vw,3.75rem)] font-light leading-none tracking-tight text-brand-ink"
              />
              <Reveal>
                <p className="font-display text-xl italic text-brand-teal md:text-2xl">
                  Your care starts before the massage begins.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.1}>
              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-brand-moss md:text-base">
                From the moment you schedule your first appointment, our goal is to make your
                experience feel simple, welcoming, and comfortable.
              </p>
            </Reveal>
            <div className="mt-12 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
              {FIRST_VISIT.map((step, i) => (
                <Reveal key={step.num} delay={i * 0.1}>
                  <div className="border-t-2 border-brand-sage pt-6" data-testid={`first-visit-step-${i}`}>
                    <span className="font-display text-4xl font-light italic text-brand-teal/70">
                      {step.num}
                    </span>
                    <h3 className="mt-3 text-base font-semibold text-brand-ink">{step.title}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-brand-moss">{step.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-brand-cream pb-24 md:pb-32" data-testid="contact-section">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-5 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="space-y-4">
              <Reveal>
                <a
                  data-testid="contact-card-address"
                  href={MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-start gap-5 rounded-3xl bg-brand-parchment p-7 transition-transform duration-500 hover:-translate-y-1"
                >
                  <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-2xl bg-brand-teal text-brand-cream">
                    <MapPin className="size-5" />
                  </span>
                  <span>
                    <span className="block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-teal">
                      Visit the studio
                    </span>
                    <span className="mt-1.5 block text-sm font-medium leading-relaxed text-brand-ink">
                      {ADDRESS_LINE_1}
                      <br />
                      {ADDRESS_LINE_2}
                    </span>
                    <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-brand-teal">
                      Get directions
                      <ArrowUpRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </span>
                </a>
              </Reveal>
              <Reveal delay={0.1}>
                <a
                  data-testid="contact-card-phone"
                  href={PHONE_HREF}
                  className="group flex items-start gap-5 rounded-3xl bg-brand-parchment p-7 transition-transform duration-500 hover:-translate-y-1"
                >
                  <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-2xl bg-brand-teal text-brand-cream">
                    <Phone className="size-5" />
                  </span>
                  <span>
                    <span className="block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-teal">
                      Call or text
                    </span>
                    <span className="mt-1.5 block text-sm font-medium text-brand-ink">{PHONE}</span>
                  </span>
                </a>
              </Reveal>
              <Reveal delay={0.2}>
                <a
                  data-testid="contact-card-email"
                  href={`mailto:${EMAIL}`}
                  className="group flex items-start gap-5 rounded-3xl bg-brand-parchment p-7 transition-transform duration-500 hover:-translate-y-1"
                >
                  <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-2xl bg-brand-teal text-brand-cream">
                    <Mail className="size-5" />
                  </span>
                  <span>
                    <span className="block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-teal">
                      Email
                    </span>
                    <span className="mt-1.5 block break-all text-sm font-medium text-brand-ink">
                      {EMAIL}
                    </span>
                  </span>
                </a>
              </Reveal>
              <Reveal delay={0.3}>
                <div data-testid="contact-card-hours" className="flex items-start gap-5 rounded-3xl bg-brand-parchment p-7">
                  <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-2xl bg-brand-teal text-brand-cream">
                    <Clock className="size-5" />
                  </span>
                  <span>
                    <span className="block text-[11px] font-bold uppercase tracking-[0.2em] text-brand-teal">
                      Hours
                    </span>
                    <span className="mt-1.5 block text-sm font-medium leading-relaxed text-brand-ink" data-testid="contact-hours">
                      {HOURS.map((h) => (
                        <span key={h.day} className="flex justify-between gap-6">
                          <span>{h.day}</span>
                          <span>{h.time}</span>
                        </span>
                      ))}
                      <span className="mt-1 flex justify-between gap-6 text-brand-moss">
                        <span>Sat – Sun</span>
                        <span>Closed</span>
                      </span>
                    </span>
                  </span>
                </div>
              </Reveal>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="rounded-3xl border border-brand-ink/10 bg-brand-parchment/60 p-7 md:p-10"
              >
                <MaskedLinesOnView
                  as="h2"
                  lines={['Send a message.']}
                  className="font-display text-3xl font-light tracking-tight text-brand-ink md:text-4xl"
                />
                <p className="mt-3 text-sm leading-relaxed text-brand-moss">
                  We typically reply within one business day. Need a table sooner?{' '}
                  <a
                    href={JANE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-form-jane-link"
                    className="font-semibold text-brand-teal underline decoration-brand-sage underline-offset-4"
                  >
                    Book a massage online
                  </a>
                  .
                </p>
                <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="cf-name" className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-brand-moss">
                      Name *
                    </label>
                    <input
                      id="cf-name"
                      data-testid="contact-input-name"
                      required
                      value={form.name}
                      onChange={set('name')}
                      className={inputClass}
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="cf-email" className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-brand-moss">
                      Email *
                    </label>
                    <input
                      id="cf-email"
                      data-testid="contact-input-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={set('email')}
                      className={inputClass}
                      placeholder="you@example.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="cf-phone" className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-brand-moss">
                      Phone (optional)
                    </label>
                    <input
                      id="cf-phone"
                      data-testid="contact-input-phone"
                      type="tel"
                      value={form.phone}
                      onChange={set('phone')}
                      className={inputClass}
                      placeholder="(832) 000-0000"
                    />
                  </div>
                  <div>
                    <label htmlFor="cf-topic" className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-brand-moss">
                      Topic
                    </label>
                    <select
                      id="cf-topic"
                      data-testid="contact-input-topic"
                      value={form.topic}
                      onChange={set('topic')}
                      className={inputClass}
                    >
                      {TOPICS.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cf-message" className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-brand-moss">
                      Message *
                    </label>
                    <textarea
                      id="cf-message"
                      data-testid="contact-input-message"
                      required
                      rows={5}
                      value={form.message}
                      onChange={set('message')}
                      className={`${inputClass} resize-none`}
                      placeholder="How can we help?"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={sending}
                  className="group mt-8 inline-flex items-center gap-2.5 rounded-full bg-brand-teal px-8 py-4 text-sm font-semibold text-brand-cream shadow-[0_14px_34px_-14px_rgba(32,86,82,0.65)] transition-[background-color,transform,opacity] duration-300 hover:bg-brand-ink active:scale-[0.97] disabled:opacity-60"
                >
                  {sending ? 'Sending…' : 'Send message'}
                  <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <p className="mt-5 max-w-xl text-xs leading-relaxed text-brand-moss/80" data-testid="contact-sms-consent">
                  By providing your mobile phone number and submitting this form, you consent to
                  receive SMS/text messages from Thula Doula Massage LLC d/b/a Thula Doula Wellness
                  regarding your inquiry, appointments, scheduling, rescheduling, cancellations,
                  customer care, and other service-related communications. Message frequency may
                  vary. Message and data rates may apply. Reply STOP to opt out or HELP for
                  assistance. Consent to receive text messages is not a condition of purchasing
                  goods or services. No mobile opt-in information or text-message consent will be
                  shared with third parties or affiliates for marketing or promotional purposes.
                </p>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-brand-cream pb-24 md:pb-32" data-testid="contact-social">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <div className="flex flex-col items-start justify-between gap-8 rounded-3xl bg-brand-parchment p-8 md:flex-row md:items-center md:p-10">
              <div>
                <MaskedLinesOnView
                  as="h2"
                  lines={['Follow along.']}
                  className="font-display text-3xl font-light tracking-tight text-brand-ink md:text-4xl"
                />
                <p className="mt-3 max-w-md text-sm leading-relaxed text-brand-moss">
                  Session openings, behind-the-scenes, and wellness education —{' '}
                  <span className="font-semibold text-brand-ink">@thuladoulamassagehtx</span>.
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <a
                  data-testid="contact-instagram"
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Thula Doula Wellness on Instagram"
                  className="inline-flex size-12 items-center justify-center rounded-full bg-brand-teal text-brand-cream transition-[background-color,transform] duration-300 hover:bg-brand-ink hover:-translate-y-0.5"
                >
                  <Instagram className="size-5" />
                </a>
                <a
                  data-testid="contact-facebook"
                  href={FACEBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Thula Doula Wellness on Facebook"
                  className="inline-flex size-12 items-center justify-center rounded-full bg-brand-teal text-brand-cream transition-[background-color,transform] duration-300 hover:bg-brand-ink hover:-translate-y-0.5"
                >
                  <Facebook className="size-5" />
                </a>
                <a
                  data-testid="contact-google-review"
                  href={GOOGLE_REVIEW_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-brand-ink/25 px-5 py-3 text-xs font-bold uppercase tracking-[0.12em] text-brand-ink transition-[background-color,color,border-color] duration-300 hover:border-brand-ink hover:bg-brand-ink hover:text-brand-cream"
                >
                  <Star className="size-4 text-brand-teal" />
                  Review us on Google
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
